import { Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuthService } from "@app/services/auth.service";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import { SocialLoginComponent } from "@app/components/auth/social-login/social-login.component";
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { RegisterLoginComponent } from '../register-login/register-login.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize } from 'rxjs';
import { customPasswordValidator } from '@app/shared/utils/form';
import { DownloadLinksComponent } from '@app/components/download-links/download-links.component';
import { AlertComponent } from '@app/components/alert/alert.component';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, MatInputModule, MatButtonModule, SocialLoginComponent, ReactiveFormsModule, MatProgressSpinnerModule, DownloadLinksComponent, AlertComponent],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent {
  fb = inject(FormBuilder)
  authService = inject(AuthService)
  registerForm: FormGroup;
  dialogRef = inject(MatDialogRef<RegisterLoginComponent>);
  isLoading = signal(false);

  successMessage = '';
  errorMessage = '';

  constructor(private dialog: MatDialog) {
    this.registerForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      displayName: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8), customPasswordValidator()]]
    });
  }

  register(): void {
    if (this.registerForm.valid) {
      this.isLoading.set(true);

      this.successMessage = '';
      this.errorMessage = '';

      const { email, displayName, password } = this.registerForm.value;
      this.authService.register(email, displayName, password)
        .pipe(
          finalize(() => {
            this.isLoading.set(false);
          })
        )
        .subscribe({
          next: (res) => {
            // Account already exists and is verified — log straight in.
            if (res.status === 'EXISTING_VERIFIED') {
              if (res.customToken) {
                this.authService.loginWithToken(res.customToken).subscribe({
                  next: () => {
                    this.dialogRef.close();
                    window.location.reload();
                  },
                  error: () => {
                    this.errorMessage = 'An account with this email already exists. Please sign in.';
                  },
                });
              } else {
                this.errorMessage = 'An account with this email already exists. Please sign in.';
              }
              return;
            }

            // CREATED or EXISTING_UNVERIFIED — backend sent an OTP; go verify.
            // Carry email + password so verify can sign the user in afterwards.
            this.dialogRef.close();
            this.dialog.open(RegisterLoginComponent, {
              maxWidth: '1000px',
              data: {
                type: 'verify',
                email,
                password,
              },
            });
          },
          error: (error: HttpErrorResponse) => {
            this.isLoading.set(false);
            if (error.status === 409 || error.status === 401) {
              this.errorMessage = 'Email already in use';
            } else if (error.status === 400) {
              this.errorMessage = error.error?.message || 'Please check your details and try again.';
            } else {
              this.errorMessage = 'Something went wrong';
            }
          },
        });
    }
  }

  onRegister(event: Event) {
    event.preventDefault();
    this.register();
  }

  openSignInForm() {
    this.dialogRef.close();
    this.dialog.open(RegisterLoginComponent, {
      maxWidth: '1000px',
      data: {
        type: 'login',
      },
    });
  }
}
